/**
 * ConsentManager — Smart Consents™ admin widget for ClientProfile.
 *
 * Features:
 *  - Lists consent requests for a client (latest first)
 *  - Status pills: pending / signed / expired
 *  - "Richiedi firma" → dialog with template multi-select
 *  - Share dialog: Copia link, WhatsApp, Email (mailto:), QR code
 *  - Download PDF for signed consents
 *  - Skeleton loading, sonner toasts, empty state
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import {
  FileSignature, Plus, Loader2, Download, Copy, MessageSquare, Mail,
  QrCode, ShieldCheck, Clock, AlertTriangle, CheckCircle2, ExternalLink,
  Sparkles,
} from "lucide-react";

const STATUS_META = {
  pending: {
    label: "In attesa",
    cls:   "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    icon:  Clock,
  },
  signed: {
    label: "Firmato",
    cls:   "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    icon:  CheckCircle2,
  },
  expired: {
    label: "Scaduto",
    cls:   "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    icon:  AlertTriangle,
  },
};

const ConsentManager = ({ clientId, apiBase = "/dental/consents" }) => {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [openReq, setOpenReq]   = useState(false);
  const [share, setShare]       = useState(null);  // { token, public_url, share }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}${apiBase}/client/${clientId}`, { withCredentials: true });
      setItems(r.data.consents || []);
    } catch {
      toast.error("Errore caricamento consensi");
    } finally {
      setLoading(false);
    }
  }, [clientId, apiBase]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="consent-manager">
      {/* Header w/ CTA */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <FileSignature className="w-3.5 h-3.5" /> Consensi digitali
          </div>
          <p className="text-[10px] text-slate-400 mt-0.5">{items.length} {items.length === 1 ? "richiesta" : "richieste"}</p>
        </div>
        <Button
          size="sm"
          onClick={() => setOpenReq(true)}
          className="h-7 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white text-[11px] px-3"
          data-testid="consent-request-open-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> Richiedi firma
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[0,1].map(i => (
            <div key={i} className="h-14 rounded-xl bg-slate-100 dark:bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-4 text-center">
          <div className="mx-auto w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-100 to-cyan-100 dark:from-violet-900/30 dark:to-cyan-900/30 flex items-center justify-center mb-2">
            <Sparkles className="w-4 h-4 text-violet-500" />
          </div>
          <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Nessun consenso ancora</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Crea il primo invio per ottenere la firma digitale.</p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map(c => <ConsentRow key={c.consent_id} c={c} apiBase={apiBase} onChanged={load} />)}
        </ul>
      )}

      {/* Request dialog */}
      <RequestDialog
        open={openReq}
        onOpenChange={setOpenReq}
        clientId={clientId}
        apiBase={apiBase}
        onCreated={(payload) => {
          setOpenReq(false);
          setShare(payload);
          load();
        }}
      />

      {/* Share dialog */}
      <ShareDialog
        share={share}
        onClose={() => setShare(null)}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
const ConsentRow = ({ c, apiBase = "/dental/consents", onChanged }) => {
  const meta = STATUS_META[c.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}${apiBase}/${c.consent_id}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `consenso-${c.consent_id.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch {
      toast.error("Errore download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const reshare = () => {
    const baseUrl = window.location.origin;
    const publicUrl = `/sign/${c.token}`;
    onChanged?.();
    const payload = {
      public_url: publicUrl,
      token: c.token,
      share: {
        whatsapp_text: `Per favore firma i moduli a questo link: ${baseUrl}${publicUrl}`,
        email_subject: "Firma digitale moduli",
        email_body:    `Per favore firma i moduli a questo link: ${baseUrl}${publicUrl}`,
      },
    };
    // hand off to global event
    window.dispatchEvent(new CustomEvent("consent:reshare", { detail: payload }));
  };

  const names = (c.templates || []).map(t => t.name).join(" · ") || "—";
  const date  = c.signed_at
    ? `Firmato il ${new Date(c.signed_at).toLocaleDateString("it-IT")}`
    : c.expires_at
    ? `Scade il ${new Date(c.expires_at).toLocaleDateString("it-IT")}`
    : "";

  return (
    <li
      data-testid={`consent-row-${c.consent_id}`}
      className="group rounded-xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-white/[0.02] p-2.5 hover:border-violet-300 transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{names}</p>
          <p className="text-[10px] text-slate-400 truncate">{date}</p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${meta.cls}`}>
          <Icon className="w-2.5 h-2.5" /> {meta.label}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        {c.status === "signed" && (
          <Button
            size="sm" variant="outline"
            onClick={downloadPdf}
            disabled={downloading}
            className="h-6 rounded-full text-[10px] px-2"
            data-testid={`consent-pdf-btn-${c.consent_id}`}
          >
            {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            <span className="ml-1">PDF</span>
          </Button>
        )}
        {c.status === "pending" && (
          <Button
            size="sm" variant="outline"
            onClick={reshare}
            className="h-6 rounded-full text-[10px] px-2"
            data-testid={`consent-reshare-btn-${c.consent_id}`}
          >
            <ExternalLink className="w-3 h-3 mr-1" /> Ricondividi
          </Button>
        )}
      </div>
    </li>
  );
};

/* ------------------------------------------------------------------ */
/* REQUEST DIALOG                                                      */
/* ------------------------------------------------------------------ */
const RequestDialog = ({ open, onOpenChange, clientId, apiBase = "/dental/consents", onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected]   = useState({});
  const [loading, setLoading]     = useState(false);
  const [creating, setCreating]   = useState(false);
  const [expiresDays, setExpiresDays] = useState(30);

  useEffect(() => {
    if (!open) return;
    setSelected({});
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}${apiBase}/templates`, { withCredentials: true });
        setTemplates(r.data.templates || []);
      } catch {
        toast.error("Errore caricamento template");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, apiBase]);

  const chosen = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([id]) => id),
    [selected]
  );

  const submit = async () => {
    if (chosen.length === 0) {
      toast.error("Seleziona almeno un modulo");
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}${apiBase}/request`, {
        client_id: clientId,
        template_ids: chosen,
        expires_in_days: Number(expiresDays) || 30,
      }, { withCredentials: true });
      toast.success("Richiesta firma creata!");
      onCreated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione richiesta");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="consent-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
              <FileSignature className="w-3.5 h-3.5 text-white" />
            </div>
            Richiedi firma digitale
          </DialogTitle>
          <DialogDescription>
            Seleziona i moduli da inviare al paziente. Verrà generato un link sicuro da condividere.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2 py-2">
            {[0,1,2].map(i => <div key={i} className="h-12 rounded-xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-2 max-h-[40vh] overflow-y-auto py-1">
            {templates.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Nessun template attivo</p>
            ) : templates.map(t => {
              const on = !!selected[t.template_id];
              return (
                <label
                  key={t.template_id}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    on
                      ? "border-violet-500 bg-violet-50/60 dark:bg-violet-900/20"
                      : "border-slate-200 dark:border-white/10 hover:border-slate-300"
                  }`}
                  data-testid={`consent-template-option-${t.template_id}`}
                >
                  <Checkbox
                    checked={on}
                    onCheckedChange={v => setSelected(s => ({ ...s, [t.template_id]: !!v }))}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">{t.name}</p>
                    <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{t.body}</p>
                    {t.is_default && (
                      <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">
                        Predefinito
                      </span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 text-xs pt-2 border-t border-slate-100 dark:border-white/10">
          <label className="text-slate-500">Scadenza link (giorni)</label>
          <input
            type="number" min={1} max={365}
            value={expiresDays}
            onChange={e => setExpiresDays(e.target.value)}
            className="w-20 h-8 rounded-lg border border-slate-200 dark:border-white/10 bg-transparent px-2 text-sm text-right"
            data-testid="consent-expires-days"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={creating || chosen.length === 0}
            className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white"
            data-testid="consent-create-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
            {creating ? "Creazione…" : `Genera link${chosen.length ? ` (${chosen.length})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* SHARE DIALOG  (Copy / WhatsApp / Email / QR)                       */
/* ------------------------------------------------------------------ */
const ShareDialog = ({ share, onClose }) => {
  const [showQr, setShowQr] = useState(false);

  // listen for re-share events
  const [external, setExternal] = useState(null);
  useEffect(() => {
    const handler = (e) => setExternal(e.detail);
    window.addEventListener("consent:reshare", handler);
    return () => window.removeEventListener("consent:reshare", handler);
  }, []);

  const active = share || external;
  const open = !!active;

  const close = () => {
    onClose?.();
    setExternal(null);
    setShowQr(false);
  };

  if (!active) return null;

  const baseUrl = window.location.origin;
  const fullUrl = `${baseUrl}${active.public_url}`;
  const waText  = (active.share?.whatsapp_text || `Firma qui: ${fullUrl}`).replace("{base_url}", baseUrl);
  const emailSubject = active.share?.email_subject || "Firma digitale moduli";
  const emailBody    = (active.share?.email_body || waText).replace("{base_url}", baseUrl);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copiato");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="consent-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
            </div>
            Link pronto da condividere
          </DialogTitle>
          <DialogDescription>
            Invialo al paziente o mostragli il QR in studio.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-2.5 text-xs font-mono break-all text-slate-700 dark:text-slate-200">
          {fullUrl}
        </div>

        {showQr && (
          <div className="flex flex-col items-center gap-2 py-2">
            <div className="p-3 rounded-2xl bg-white border border-slate-200">
              <QRCodeSVG value={fullUrl} size={180} level="M" />
            </div>
            <p className="text-[10px] text-slate-400">Inquadralo con la fotocamera del paziente</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <ShareBtn icon={Copy} label="Copia link" onClick={copy} testid="consent-share-copy" />
          <ShareBtn
            icon={MessageSquare}
            label="WhatsApp"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank")}
            testid="consent-share-whatsapp"
            accent="emerald"
          />
          <ShareBtn
            icon={Mail}
            label="Email"
            onClick={() => window.open(`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`)}
            testid="consent-share-email"
            accent="blue"
          />
          <ShareBtn
            icon={QrCode}
            label={showQr ? "Nascondi QR" : "QR code"}
            onClick={() => setShowQr(v => !v)}
            testid="consent-share-qr"
            accent="violet"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ACCENT_CLS = {
  default:  "from-slate-100 to-slate-50 dark:from-white/5 dark:to-white/0",
  emerald:  "from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20",
  blue:     "from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20",
  violet:   "from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20",
};

const ShareBtn = ({ icon: Icon, label, onClick, testid, accent = "default" }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`group flex items-center gap-2 px-3 py-3 rounded-2xl border border-slate-200 dark:border-white/10 bg-gradient-to-br ${ACCENT_CLS[accent]} hover:scale-[1.02] active:scale-[0.98] transition-all`}
  >
    <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{label}</span>
  </button>
);

export default ConsentManager;
